/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { toast } from "react-toastify";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import JournalVoucher6Info from "./info";
import journalVoucher6Service from "services/journal-voucher-6";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const emptyRow = () => ({
  index: 0,
  voucherNumber: "",
  voucherDate: "",
  description: "",
  debitValues334: Array(10).fill(0),
  totalCredit334: 0,
  debitValues338: Array(10).fill(0),
  totalCredit338: 0,
});

const initialValues = {
  unit: "",
  address: "",
  month: (today.getMonth() + 1).toString(),
  year: today.getFullYear().toString(),
  debitAccountLabels: Array(10).fill(""),
  rows: [emptyRow()],
  totals: {
    debitValues334: Array(10).fill(0),
    totalCredit334: 0,
    debitValues338: Array(10).fill(0),
    totalCredit338: 0,
  },
  recordedToLedgerDay: "",
  recordedToLedgerMonth: "",
  recordedToLedgerYear: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function JournalVoucher6Detail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  const isReadOnly = containView || formInitials.autoMapped;

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await journalVoucher6Service.findById(id);
          const apiData = response.data || response;

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...apiData,
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/journal-voucher-6");

  const num = (v) => (v !== undefined && v !== "" && !isNaN(v) ? Number(v) : 0);

  const handleSync = async (fValues, setFieldValue) => {
    try {
      const response = await journalVoucher6Service.preview({
        company: fValues.company?._id || fValues.company,
        month: fValues.month,
        year: fValues.year,
      });
      const data = response.data || response;

      if (data) {
        setFieldValue("unit", data.unit || fValues.unit);
        setFieldValue("address", data.address || fValues.address);
        setFieldValue("rows", data.rows || []);
        setFieldValue("totals", data.totals || initialValues.totals);
        setFieldValue("debitAccountLabels", data.debitAccountLabels || initialValues.debitAccountLabels);
        toast.success("Đã lấy dữ liệu thành công!");
      }
    } catch (e) {
      console.error("Sync failed:", e);
      toast.error("Không thể lấy dữ liệu đồng bộ!");
    }
  };

  const submitForm = async (fValues) => {
    try {
      const payload = {
        company: fValues.company?._id || fValues.company,
        unit: fValues.unit,
        address: fValues.address,
        month: fValues.month,
        year: fValues.year,
        debitAccountLabels: fValues.debitAccountLabels,
        rows: (fValues.rows || []).map((r, idx) => ({
          ...r,
          index: r.index || idx + 1,
          debitValues334: (r.debitValues334 || []).map(num),
          totalCredit334: num(r.totalCredit334),
          debitValues338: (r.debitValues338 || []).map(num),
          totalCredit338: num(r.totalCredit338),
        })),
        totals: {
          debitValues334: (fValues.totals.debitValues334 || []).map(num),
          totalCredit334: num(fValues.totals.totalCredit334),
          debitValues338: (fValues.totals.debitValues338 || []).map(num),
          totalCredit338: num(fValues.totals.totalCredit338),
        },
        recordedToLedgerDay: fValues.recordedToLedgerDay,
        recordedToLedgerMonth: fValues.recordedToLedgerMonth,
        recordedToLedgerYear: fValues.recordedToLedgerYear,
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await journalVoucher6Service.create(payload);
        toast.success("Tạo nhật ký chứng từ thành công!");
      } else {
        await journalVoucher6Service.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/journal-voucher-6");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>In NKCT số 6</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, setFieldValue }) => (
                <Form>
                  <Card sx={{ overflow: "visible" }}>
                    <SoftBox p={3}>
                      <JournalVoucher6Info formData={{ values, setFieldValue, containView: isReadOnly }} />
                    </SoftBox>
                    {!isReadOnly ? (
                      <SoftBox px={3} pb={3} display="flex" justifyContent="flex-end" gap={2}>
                        <SoftButton
                          variant="outlined"
                          color="info"
                          onClick={() => handleSync(values, setFieldValue)}
                        >
                          Lấy dữ liệu
                        </SoftButton>
                        <SoftButton
                          variant="outlined"
                          color="dark"
                          onClick={handleBack}
                        >
                          Quay lại
                        </SoftButton>
                        <SoftButton
                          type="submit"
                          variant="gradient"
                          color="success"
                        >
                          {id ? "Lưu" : "Tạo"}
                        </SoftButton>
                        {id && (
                          <SoftButton
                            variant="gradient"
                            color="info"
                            onClick={() => handlePrint(values)}
                          >
                            In
                          </SoftButton>
                        )}
                      </SoftBox>
                    ) : (
                      <SoftBox px={3} pb={3} display="flex" justifyContent="flex-end" gap={2}>
                        <SoftButton
                          variant="gradient"
                          color="info"
                          onClick={() => handlePrint(values)}
                        >
                          In
                        </SoftButton>
                        <SoftButton
                          variant="outlined"
                          color="dark"
                          onClick={handleBack}
                        >
                          Quay lại
                        </SoftButton>
                      </SoftBox>
                    )}
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default JournalVoucher6Detail;
